import { StateDefinitions } from '@iwentsourcing/core/storage/state-declaration.js';
import { SnapshotClient } from '@iwentsourcing/core/storage/snapshot-client.js';
import { createRemoteSnapshotClient } from './create-remote-snapshot-client.js';
import { createRemoteObservedClient } from './create-remote-observed-client.js';
import { RemoteDescription } from './remote-description.js';
import { RpcClient } from '../api/create-rpc-client.js';
import { SessionManager } from '../auth/jwt-session.js';
import { Lazy } from '@iwentsourcing/core/lazy/lazy.js';
import { AggregateInterface } from '@iwentsourcing/core/aggregation/aggregate-interface.js';
import { ObservedClient } from '@iwentsourcing/core/storage/observer/observed-client.js';
import { Telemetry } from '@iwentsourcing/core/tracer/telemetry.js';

export function createRemoteAggregationInterface<
  TState extends StateDefinitions,
>(
  state: TState,
  rpc: RpcClient,
  session: SessionManager,
  lazyVersion: Lazy<string>,
  aggregation: RemoteDescription,
  telemetry: Telemetry,
): AggregateInterface<TState> {
  return {
    state,
    observe<K extends string & keyof TState>(
      name: K,
    ): ObservedClient<TState[K]> {
      return createRemoteObservedClient(
        state[name]!,
        rpc,
        session,
        lazyVersion,
        'aggregation',
        aggregation,
        name,
        telemetry,
      );
    },
    snapshot<K extends string & keyof TState>(
      name: K,
    ): SnapshotClient<TState[K]> {
      return createRemoteSnapshotClient(
        state[name]!,
        rpc,
        session,
        lazyVersion,
        'aggregation',
        aggregation,
        name,
        telemetry,
      );
    },
    async ensure(eventId: string, signal: AbortSignal) {
      await rpc['process.await'].call(
        {
          tenant: aggregation.tenant,
          realm: aggregation.realm,
          version: await lazyVersion.resolve(),
          pattern: aggregation.pattern,
          token: await session.resolveToken(),
          eventId: eventId,
          args: aggregation.args,
        },
        signal,
      );
    },
  };
}
